// CreateUserFlow.jsx
import React, { useEffect, useState } from "react";
import { Modal, Typography, Radio, Space } from "antd";
import Add from "./Add";
import Password from "./Password";
import OtpModal from "./OtpModal";
import { useApiCall } from "../../services/api";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { useSelector } from "react-redux";
import { roleIds, roleNames } from "../../constant";
import { useLocation, useNavigate } from "react-router-dom";
import PrimaryButton from "../Forms/PrimaryButton";
import { handleRouteNav } from "../../utils";

const { Text, Title } = Typography;

const DEFAULT_ROLES = Object.entries(roleNames).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const CreateUserFlow = ({
  formType,
  isModalVisible,
  setIsModalVisible,
  // only boolean parent uses to request role step appear as step 1
  openRoleSelectorOnOpen = false,
  handleSubmit,
}) => {
  const { postApi, loading } = useApiCall();
  const [formData, setFormData] = useState({});
  // step numbering is dynamic: when role step enabled, steps are 1..4, otherwise 1..3
  const [step, setStep] = useState(1);
  const { showPopup } = useGlobalModal();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    full_name,
    user_id,
    role_id: currentUserRoleId,
    is_admin,
  } = useSelector((state) => state.auth);

  // internal role chosen for the new user (kept local)
  const [newUserRole, setNewUserRole] = useState(null);

  // activeFormType drives Add/Password/Otp rendering; parent-provided formType is used to initialize
  const [activeFormType, setActiveFormType] = useState(formType);

  // whether role step is active for this session
  const roleStepEnabled = !!openRoleSelectorOnOpen;

  // Close modal when step exceeds dynamic max
  useEffect(() => {
    const maxStep = roleStepEnabled ? 4 : 3;
    if (step > maxStep) {
      setIsModalVisible(false);
    }
  }, [step, roleStepEnabled, setIsModalVisible]);

  // Reset when parent changes formType (keeps previous behavior)
  useEffect(() => {
    setStep(1);
    setFormData({});
    setNewUserRole(null);
    setActiveFormType(formType);
  }, [formType]);

  // ---------- existing helpers (unchanged logic but use activeFormType instead of prop formType) ----------
  const handleResendOtp = async (type = "email", showResponse = false) => {
    const data = { [type]: formData[type], deviceType: "Web" };
    if (type === "phone") {
      data["email"] = formData["email"];
    }

    const apiRoute =
      activeFormType === "client"
        ? `customer/register-send-otp-${type}`
        : `sales/employee/signup-send-otp-${type}`;
    const res = await postApi(apiRoute, data, showResponse);

    if (res && res?.status) {
      return true;
    }
    return false;
  };

  const handleFinish = async (values) => {
    let response, body, apiRoute;
    if (activeFormType === "Employee" || activeFormType === "Admin") {
      body = {
        ...formData,
        ...values,
        role_id: (newUserRole || formData.role_id) ?? roleIds.SalesPerson,
        office_address_type: "office",
        residential_address_type: "residential",
      };
      apiRoute = "sales/employee/signup";
    } else if (activeFormType === "client") {
      apiRoute = "customer/register";
      body = {
        ...formData,
        ...values,
        user_id: 0,
        updated_by: full_name,
        updated_by_id: user_id,
      };
    } else {
      // fallback: treat as employee if activeFormType is missing
      body = {
        ...formData,
        ...values,
        role_id: (newUserRole || formData.role_id) ?? roleIds.SalesPerson,
      };
      apiRoute = "sales/employee/signup";
    }

    response = await postApi(apiRoute, body, true, null, () => {
      setStep(1);
      setIsModalVisible(false);
      setFormData({});
      handleSubmit?.();

      const targetPath =
        activeFormType === "client"
          ? handleRouteNav("/pending-clients", currentUserRoleId)
          : handleRouteNav(
              is_admin ? "/users" : "/sales-employees",
              currentUserRoleId,
            );

      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    });
  };

  const handleCheckAcc = async ({ email, phone }) => {
    const apiRoute =
      activeFormType === "client"
        ? "customer/check-duplicate-customer"
        : "user/check-duplicate-user";
    const res = await postApi(apiRoute, { email, phone });
    if (!res?.status) {
      return showPopup({
        title: null,
        content: <span className="custom-modal-title">{res.message}</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
    }
    // progress to next step in dynamic flow
    setStep((prev) => prev + 1);
  };

  const handleSendOtps = async () => {
    const sentEmail = await handleResendOtp("email");
    const sentPhone = await handleResendOtp("phone");
    if (!sentEmail || !sentPhone) {
      return showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">
            Failed to send OTP to the provided {!sentEmail ? "email" : ""}
            {!sentPhone ? (!sentEmail ? " and phone" : "phone") : ""}.
          </span>
        ),
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
    }
    return showPopup({
      title: null,
      content: (
        <span className="custom-modal-title">
          Please enter the OTPs sent to the provided phone and email.
        </span>
      ),
      onOk: () => setStep((prev) => prev + 1),
      className: "custom-success-modal",
    });
  };
  // --------------------------------------------------------------------------

  const onClose = () => {
    setIsModalVisible(false);
  };

  // When user confirms role selection (role step), merge role and advance to Add
  const confirmRoleSelection = (selectedRole) => {
    setNewUserRole(selectedRole);
    setFormData((prev) => ({ ...prev, role_id: selectedRole }));
    // set active form type according to role
    if (selectedRole === roleIds.Customer) {
      setActiveFormType("client");
    } else if (
      [roleIds.SalesPerson, roleIds.SalesManager].includes(selectedRole)
    ) {
      setActiveFormType("Employee");
    } else if ([roleIds.Admin].includes(selectedRole)) {
      // default to Employee for other roles
      setActiveFormType("Admin");
    }
    // advance to next step which is Add
    setStep((prev) => prev + 1);
  };

  // render role selection UI inline (step 1 when roleStepEnabled)
  const RoleStep = () => {
    const initialRole =
      formData?.role_id !== undefined && formData?.role_id !== null
        ? Number(formData.role_id)
        : DEFAULT_ROLES[0]?.value;

    const [selectedRole, setSelectedRole] = useState(initialRole);

    useEffect(() => {
      // sync if formData.role_id changes while open
      if (formData?.role_id !== undefined && formData?.role_id !== null) {
        setSelectedRole(Number(formData.role_id));
      }
    }, [formData?.role_id]);

    return (
      <div>
        <Title className="font-abhaya !mb-0" level={2}>
          Select role
        </Title>
        <Radio.Group
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          style={{ width: "100%" }}
          className="mt-4"
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            {DEFAULT_ROLES.map((role) => (
              <Radio key={role.value} value={role.value}>
                {role.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        <div className="w-full flex justify-center mt-4">
          <PrimaryButton
            text="Confirm role"
            onClick={() => confirmRoleSelection(selectedRole)}
            className="me-[30px]"
          />
        </div>
      </div>
    );
  };

  // Decide which content to render for the current dynamic step
  const renderStepContent = () => {
    if (roleStepEnabled) {
      // flow: 1=Role, 2=Add, 3=Password, 4=Otp
      if (step === 1) {
        return <RoleStep />;
      }
      if (step === 2) {
        return (
          <Add
            type={activeFormType}
            title={
              activeFormType === "client"
                ? "Add a new client"
                : `Add a new ${
                    roleNames[newUserRole || formData.role_id]?.toLowerCase() ||
                    "employee"
                  }`
            }
            handleFinish={(values) => {
              setFormData({ ...formData, ...values });
              handleCheckAcc(values);
            }}
            formData={formData}
            prevStep={
              roleStepEnabled ? () => setStep((prev) => prev - 1) : null
            }
          />
        );
      }
      if (step === 3) {
        return (
          <Password
            handleFinish={(values) => {
              setFormData({ ...formData, ...values });
              handleSendOtps();
            }}
            setStep={setStep}
            formData={formData}
            setFormData={setFormData}
            formType={activeFormType}
            title={`Create a new password for ${
              activeFormType === "client"
                ? "client"
                : roleNames[newUserRole || formData.role_id]?.toLowerCase() ||
                  "employee"
            }.`}
            loading={loading}
          />
        );
      }
      if (step === 4) {
        return (
          <OtpModal
            formType={activeFormType}
            step={step}
            formData={formData}
            handleResendOtp={handleResendOtp}
            handleFinish={handleFinish}
            title={`${
              activeFormType === "client"
                ? "Client"
                : roleNames[newUserRole || formData.role_id] || "Employee"
            } Verification`}
            onPrevious={() => setStep((prev) => prev - 1)}
          />
        );
      }
    } else {
      // flow: 1=Add, 2=Password, 3=Otp
      if (step === 1) {
        return (
          <Add
            type={activeFormType}
            handleFinish={(values) => {
              setFormData({ ...formData, ...values });
              handleCheckAcc(values);
            }}
            formData={formData}
            title={
              activeFormType === "client"
                ? "Add a new client"
                : "Add a new employee"
            }
          />
        );
      }
      if (step === 2) {
        return (
          <Password
            handleFinish={(values) => {
              setFormData({ ...formData, ...values });
              handleSendOtps();
            }}
            setStep={setStep}
            formData={formData}
            setFormData={setFormData}
            formType={activeFormType}
            title={`Create a new password for ${
              activeFormType === "client" ? "client" : "employee"
            }.`}
            loading={loading}
          />
        );
      }
      if (step === 3) {
        return (
          <OtpModal
            formType={activeFormType}
            step={step}
            formData={formData}
            handleResendOtp={handleResendOtp}
            handleFinish={handleFinish}
            title={`${activeFormType === "client" ? "Client" : "Employee"} Verification`}
            onPrevious={() => setStep((prev) => prev - 1)}
          />
        );
      }
    }
    return null;
  };

  return (
    <Modal
      className="form-css w-max-[600px]"
      open={isModalVisible}
      onOk={onClose}
      footer={null}
      onCancel={onClose}
      width={openRoleSelectorOnOpen && step === 1 ? 300 : 500}
    >
      <div
        className="transition-all duration-300 ease-in-out opacity-100 translate-y-0"
        key={step}
      >
        {renderStepContent()}
      </div>
    </Modal>
  );
};

export default CreateUserFlow;
